/**
 * Runtime Telemetry: Analytics and Metrics Collection
 *
 * Collects runtime telemetry data for analytics, monitoring,
 * and operational insights.
 *
 * @platform-infrastructure
 */

import { RuntimeContext, RuntimeExecution } from '../contracts'

/**
 * Telemetry Event Types
 */
export enum TelemetryEventType {
  // User actions
  USER_ACTION = 'user_action',
  USER_NAVIGATION = 'user_navigation',
  USER_INTERACTION = 'user_interaction',

  // Application events
  APP_STARTUP = 'app_startup',
  APP_ERROR = 'app_error',
  APP_PERFORMANCE = 'app_performance',

  // Business events
  BUSINESS_OPERATION = 'business_operation',
  BUSINESS_SUCCESS = 'business_success',
  BUSINESS_FAILURE = 'business_failure',

  // System events
  SYSTEM_METRIC = 'system_metric',
  SYSTEM_HEALTH = 'system_health',
  SYSTEM_ALERT = 'system_alert',
}

/**
 * Telemetry Event
 */
export interface TelemetryEvent {
  /** Event type */
  type: TelemetryEventType

  /** Event name */
  name: string

  /** Event timestamp */
  timestamp: Date

  /** Runtime context */
  context?: RuntimeContext

  /** Event properties */
  properties?: Record<string, any>

  /** Event metrics */
  metrics?: Record<string, number>

  /** User ID (anonymized) */
  userId?: string

  /** Session ID */
  sessionId?: string
}

/**
 * Runtime Telemetry Collector
 *
 * Collects and dispatches telemetry events for analytics and monitoring.
 */
export class RuntimeTelemetryCollector {
  private static eventHandlers: Array<(event: TelemetryEvent) => void> = []
  private static enabled: boolean = true
  private static batchSize: number = 10
  private static batchTimeout: number = 5000 // 5 seconds
  private static eventQueue: TelemetryEvent[] = []
  private static batchTimer: NodeJS.Timeout | null = null

  /**
   * Enable or disable telemetry collection
   */
  static setEnabled(enabled: boolean): void {
    this.enabled = enabled
  }

  /**
   * Add a telemetry event handler
   */
  static addHandler(handler: (event: TelemetryEvent) => void): void {
    this.eventHandlers.push(handler)
  }

  /**
   * Remove a telemetry event handler
   */
  static removeHandler(handler: (event: TelemetryEvent) => void): void {
    this.eventHandlers = this.eventHandlers.filter(h => h !== handler)
  }

  /**
   * Collect a telemetry event
   */
  static collect(
    type: TelemetryEventType,
    name: string,
    context?: RuntimeContext,
    properties?: Record<string, any>,
    metrics?: Record<string, number>
  ): void {
    if (!this.enabled) return

    const event: TelemetryEvent = {
      type,
      name,
      timestamp: new Date(),
      context,
      properties,
      metrics,
      userId: context?.actor || undefined,
      sessionId: context?.sessionId,
    }

    // Add to queue
    this.eventQueue.push(event)

    // Check if we should flush the batch
    if (this.eventQueue.length >= this.batchSize) {
      this.flushBatch()
    } else if (!this.batchTimer) {
      // Start batch timer
      this.batchTimer = setTimeout(() => {
        this.flushBatch()
      }, this.batchTimeout)
    }
  }

  /**
   * Flush the current batch of events
   */
  private static flushBatch(): void {
    if (this.eventQueue.length === 0) return

    const events = [...this.eventQueue]
    this.eventQueue = []

    if (this.batchTimer) {
      clearTimeout(this.batchTimer)
      this.batchTimer = null
    }

    // Dispatch events to handlers
    this.eventHandlers.forEach(handler => {
      try {
        // Send events in batch
        handler({
          type: TelemetryEventType.SYSTEM_METRIC,
          name: 'telemetry_batch',
          timestamp: new Date(),
          properties: { eventCount: events.length },
        })

        // Send individual events
        events.forEach(event => {
          handler(event)
        })
      } catch (error) {
        console.error('Telemetry handler error:', error)
      }
    })
  }

  /**
   * Collect execution telemetry
   */
  static collectExecution(execution: RuntimeExecution): void {
    this.collect(
      TelemetryEventType.APP_PERFORMANCE,
      'execution_completed',
      execution.context,
      {
        operation: execution.operation,
        type: execution.type,
        result: execution.result,
        errorCode: execution.error?.code,
      },
      {
        duration: execution.duration || 0,
        memoryUsage: execution.metrics?.memoryUsage,
        cpuUsage: execution.metrics?.cpuUsage,
        networkRequests: execution.metrics?.networkRequests,
      }
    )
  }

  /**
   * Collect user action telemetry
   */
  static collectUserAction(
    action: string,
    context?: RuntimeContext,
    properties?: Record<string, any>
  ): void {
    this.collect(
      TelemetryEventType.USER_ACTION,
      action,
      context,
      properties
    )
  }

  /**
   * Collect navigation telemetry
   */
  static collectNavigation(
    from: string,
    to: string,
    context?: RuntimeContext,
    properties?: Record<string, any>
  ): void {
    this.collect(
      TelemetryEventType.USER_NAVIGATION,
      'page_navigation',
      context,
      {
        from,
        to,
        ...properties,
      }
    )
  }

  /**
   * Collect business operation telemetry
   */
  static collectBusinessOperation(
    operation: string,
    success: boolean,
    context?: RuntimeContext,
    properties?: Record<string, any>,
    metrics?: Record<string, number>
  ): void {
    this.collect(
      success ? TelemetryEventType.BUSINESS_SUCCESS : TelemetryEventType.BUSINESS_FAILURE,
      operation,
      context,
      properties,
      metrics
    )
  }

  /**
   * Collect error telemetry
   */
  static collectError(
    error: Error,
    context?: RuntimeContext,
    properties?: Record<string, any>
  ): void {
    this.collect(
      TelemetryEventType.APP_ERROR,
      'runtime_error',
      context,
      {
        errorMessage: error.message,
        errorName: error.name,
        stack: error.stack,
        ...properties,
      }
    )
  }

  /**
   * Collect performance telemetry
   */
  static collectPerformance(
    metric: string,
    value: number,
    context?: RuntimeContext,
    properties?: Record<string, any>
  ): void {
    this.collect(
      TelemetryEventType.APP_PERFORMANCE,
      'performance_metric',
      context,
      {
        metric,
        ...properties,
      },
      {
        [metric]: value,
      }
    )
  }

  /**
   * Get telemetry statistics
   */
  static getStats(): {
    enabled: boolean
    queuedEvents: number
    handlersCount: number
    batchSize: number
    batchTimeout: number
  } {
    return {
      enabled: this.enabled,
      queuedEvents: this.eventQueue.length,
      handlersCount: this.eventHandlers.length,
      batchSize: this.batchSize,
      batchTimeout: this.batchTimeout,
    }
  }

  /**
   * Force flush all queued events
   */
  static forceFlush(): void {
    this.flushBatch()
  }
}